// Work Center tab: the signed-in person's evaluation duties for the period, read
// from the Evaluation Responsibility registry. It complements "Actions" (what to do
// next) with the accountability record: what was owed, when, and whether it was on
// time. Doing the work still happens in the source page each row links to.
import { useCallback, useEffect, useState } from "react";
import { apiRequest } from "./api";
import { ActionItem, GroupLabel, SkeletonRows, WsEmpty } from "./WorkspaceUi";
import { evaluationResponsibilityCopy, formatErDate, ER_ACTION_TARGET } from "../components/routine/evaluationResponsibilityCopy";

const SOURCE_ICON = { PEER_FEEDBACK: "peer", CORE_REVIEW: "self", GOAL_DECISION: "target", GOAL_REVIEW: "target", ADDITIONAL_CONTRIBUTION_REVIEW: "activity", KPI_SUBMISSION: "target", KPI_APPROVAL: "inbox", UNIT_QUALITATIVE_REVIEW: "org" };
const STATUS_FOR_STATE = { OVERDUE: "overdue", DUE_SOON: "due_soon", OPEN: "my_action", ON_TIME: "info", COMPLETED_LATE: "info" };
const GROUPS = [["open", ["OVERDUE", "DUE_SOON", "OPEN"]], ["done", ["COMPLETED_LATE", "ON_TIME"]], ["closed", ["EXEMPT", "TRANSFERRED", "CANCELLED"]]];

export function groupDuties(items = []) {
    return GROUPS.map(([id, states]) => ({ id, rows: items.filter(item => states.includes(item.state)) })).filter(group => group.rows.length);
}

export function useEvaluationDuties(periodId, enabled) {
    const [state, setState] = useState({ key: null, data: null, error: false });
    const key = periodId || "";
    const load = useCallback(async () => {
        if (!enabled) return;
        try {
            const data = await apiRequest(`/api/evaluation-responsibility/me/duties${periodId ? `?periodId=${encodeURIComponent(periodId)}` : ""}`);
            setState({ key, data, error: false });
        } catch (error) {
            console.error("Evaluation duties load failed:", error);
            setState({ key, data: null, error: true });
        }
    }, [enabled, periodId, key]);
    useEffect(() => { load(); }, [load]);
    const fresh = state.key === key;
    return { data: fresh ? state.data : null, error: fresh && state.error, reload: load };
}

export function EvaluationDutiesView({ language, data, error, onRetry, navigator, onNavigate }) {
    const c = evaluationResponsibilityCopy(language);
    if (error) return <WsEmpty tone="error" icon="warning" title={c.ledger.error}
        action={<button type="button" className="cvi-ws-btn is-secondary is-sm" onClick={onRetry}>{c.retry}</button>} />;
    if (!data) return <SkeletonRows count={3} label={c.loading} />;
    if (!data.items?.length) return <WsEmpty title={c.ledger.empty[0]} description={c.ledger.empty[1]} />;
    const canOpen = target => Boolean(target && navigator?.can?.(target));
    return <div className="cvi-er-ledger">
        <p className="cvi-er-ledger-intro">{c.ledger.intro}</p>
        {groupDuties(data.items).map(group => <section key={group.id} aria-label={c.groups[group.id]}>
            <GroupLabel count={group.rows.length}>{c.groups[group.id]}</GroupLabel>
            <div className="cvi-ws-actions-list">
                {group.rows.map(duty => {
                    const target = ER_ACTION_TARGET[duty.sourceType];
                    const isOpen = group.id === "open";
                    const context = [duty.subjectName && c.forPerson(duty.subjectName), duty.label, duty.revision > 0 && c.revision(duty.revision), duty.transferredFrom && c.transferred].filter(Boolean).join(" · ");
                    return <ActionItem key={duty.id} language={language} icon={SOURCE_ICON[duty.sourceType] || "activity"}
                        title={c.sources[duty.sourceType] || duty.sourceType} context={context || null}
                        status={STATUS_FOR_STATE[duty.state] || "info"} statusLabel={c.states[duty.state]}
                        due={duty.completedAt ? c.completed(formatErDate(duty.completedAt, language)) : c.due(formatErDate(duty.dueAt, language))}
                        dueTone={duty.state === "OVERDUE" ? "danger" : duty.state === "DUE_SOON" || duty.state === "COMPLETED_LATE" ? "warning" : null}
                        cta={isOpen && canOpen(target) ? c.open : null} ctaVariant="secondary"
                        onCta={() => { if (navigator?.open?.(target, { actionTarget: target }) !== false) onNavigate?.(); }} />;
                })}
            </div>
        </section>)}
    </div>;
}
